<template>
  <div v-if="buildingStatusForm">
    <div class="section-title mb-4">สถานะการก่อสร้าง</div>
    <building-status-editor
      class="mb-5"
      v-model="buildingStatusForm"
      :imageUrls="imageUrls"
      @removeImage="handleRemoveImage"
      :disabled="isEditorDisabled"
    ></building-status-editor>
    <div class="actions text-right">
      <sc-button
        class="btn-remove"
        variant="danger"
        @click="handleClickRemove"
        v-if="this.instruction"
        >ลบ</sc-button
      >
      <sc-button
        v-if="!isEditorDisabled"
        variant="primary"
        @click="handleClickSave"
        >บันทึก</sc-button
      >
    </div>
  </div>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import { DateTime } from "luxon";
import { baanRueJaiClient } from "@/dependencies";
import { toLocaleTH } from "@/lib/dateTime";
import { uploadPublicImage } from "@/god";
import BuildingStatusEditor from "../components/BuildingStatusEditor";

export default {
  components: {
    ScButton,
    BuildingStatusEditor
  },
  data() {
    const imageUrls = [];
    const buildingStatusForm = null;
    const instruction = null;

    return { buildingStatusForm, instruction, imageUrls };
  },
  computed: {
    isEditorDisabled() {
      return this.$isCallCenter;
    }
  },
  created() {
    this.setup();
  },
  methods: {
    async setup() {
      this.setupData();
    },
    async setupData() {
      await this.setupInstruction();
      await this.setupBuildingStatusForm();
      await this.setupImageUrls();
    },
    async setupInstruction() {
      const instruction = await this.fetchInstruction();

      this.instruction = instruction;
    },
    setupBuildingStatusForm() {
      if (!this.instruction) {
        this.buildingStatusForm = {
          startAt: null,
          endAt: null,
          updatedAt: null,
          tasks: [],
          status: null,
          overallProgress: null,
          imageFiles: []
        };
        return;
      }

      const {
        project_id,
        project_instruction_status,
        project_start_at,
        project_end_at,
        project_latest_update_at,
        project_overall_progress,
        project_detail_progress
      } = this.instruction;

      this.buildingStatusForm = {
        startAt: this.isoStringToDateTime(project_start_at),
        endAt: this.isoStringToDateTime(project_end_at),
        updatedAt: this.isoStringToDateTime(project_latest_update_at),
        tasks: project_detail_progress.map(p => ({
          startAt: this.isoStringToDateTime(p.start_at),
          name: p.name,
          progress: p.progress
        })),
        status: project_instruction_status,
        overallProgress: project_overall_progress,
        imageFiles: []
      };
    },
    setupImageUrls() {
      if (!this.instruction || !this.instruction.image_urls) {
        this.imageUrls = [];

        return;
      }

      this.imageUrls = this.instruction.image_urls;
    },
    async fetchInstruction() {
      const projectId = this.$route.params.id;
      const response = await baanRueJaiClient.admin.projects.getInstructionProgress(
        projectId
      );

      return response.data.result;
    },
    async handleClickSave() {
      const isUpdated = Boolean(this.instruction);

      try {
        const params = await this.requestParams();
        if (isUpdated) {
          await this.updateProgress(params);
        } else {
          await this.createProgress(params);
        }

        alert("บันทึกสำเร็จ");
      } catch (error) {
        console.error(error);
        if (error instanceof TypeError) {
          alert("กรุณากรอกข้อมูลให้ครบ");
          return;
        }

        alert("เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง");
      }
    },
    async createProgress(params) {
      const projectId = this.$route.params.id;

      const response = await baanRueJaiClient.admin.projects.postInstructionProgress(
        projectId,
        params
      );

      return response;
    },
    async updateProgress(params) {
      const projectId = this.$route.params.id;

      const response = await baanRueJaiClient.admin.projects.putInstructionProgress(
        projectId,
        params
      );

      return response;
    },
    async requestParams() {
      const {
        status,
        startAt,
        endAt,
        updatedAt,
        overallProgress,
        tasks,
        imageFiles
      } = this.buildingStatusForm;

      const imageFileUrls = await Promise.all(
        imageFiles.map(imageFile => uploadPublicImage(imageFile))
      );
      const image_urls = [...this.imageUrls, ...imageFileUrls];
      const project_id = this.$route.params.id;
      const project_instruction_status = status;
      const project_start_at = this.dateTimeToIsoString(startAt);
      const project_end_at = this.dateTimeToIsoString(endAt);
      const project_latest_update_at = this.dateTimeToIsoString(updatedAt);
      const project_overall_progress = overallProgress ? overallProgress : null;
      const project_detail_progress = tasks.map(t => ({
        name: t.name,
        progress: t.progress || "0",
        start_at: this.dateTimeToIsoString(t.startAt)
      }));

      const params = {
        project_id,
        project_instruction_status,
        project_start_at,
        project_end_at,
        project_latest_update_at,
        project_overall_progress,
        project_detail_progress,
        image_urls
      };

      return params;
    },
    dateTimeToIsoString(dateTime) {
      if (!dateTime) {
        return null;
      }

      return dateTime.toISO();
    },
    isoStringToDateTime(text) {
      if (!text) {
        return null;
      }

      return DateTime.fromISO(text);
    },
    async handleClickRemove() {
      if (!confirm("ยืนยันการลบ")) {
        return;
      }

      const projectId = this.$route.params.id;

      const response = await baanRueJaiClient.admin.projectInstructionProgress.delete(
        projectId
      );

      await this.setupBuildingStatusForm();
      alert("สำเร็จ");

      return response;
    },
    handleRemoveImage(index) {
      this.imageUrls = this.imageUrls.filter(
        (imageUrl, imageUrlIndex) => index !== imageUrlIndex
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.container {
  padding-top: $space;
  padding-bottom: $space;
}

.detail {
  border-right: $border;
}

.label {
  font-size: 14px;
  color: #666;
}

.text {
  font-size: 16px;
  color: black;
}

.project-group {
  display: grid;
  grid-template-columns: 40px 1fr;
  grid-gap: 15px;
  align-items: center;

  > .logo {
    border: $border;
    border-radius: 6px;
  }
}

.cover {
  border: $border;
  border-radius: 6px;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
}

.actions {
  text-align: right;
}

.btn-remove {
  float: left;
}
</style>



// WEBPACK FOOTER //
// src/features/managements/modules/projects/pages/ProjectBuildingStatusPage.vue